import { useState, Fragment } from 'react';
import DOMPurify from 'dompurify';
import { ChevronRight } from 'lucide-react';
import type { ClassPerfQuestionRow } from '../../../api/types';
import { Badge, PagedFooter, SortableTh, Table, Td } from '../../../components/ui';
import { useSortableTable, SortColumn } from '../../../lib/useSortableTable';
import { usePagination } from '../../../lib/usePagination';
import { TH_RULE } from '../../../lib/tableRules';

interface Props {
  questions: ClassPerfQuestionRow[];
}

const QUESTIONS_COLUMNS: SortColumn<ClassPerfQuestionRow>[] = [
  { key: 'question', accessor: (q) => q.ques_excerpt },
  { key: 'subject', accessor: (q) => q.subject },
  { key: 'chapter', accessor: (q) => q.chapter },
  { key: 'pct_wrong', accessor: (q) => q.pct_wrong, type: 'number' },
  { key: 'avg_time', accessor: (q) => q.avg_time_seconds, type: 'number' },
];

function fmtTime(s: number | null): string {
  if (s == null) return '—';
  if (s < 60) return `${s.toFixed(0)}s`;
  return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
}

/** The wrong option the most students picked, or null when nobody picked a wrong one. */
function commonestWrong(q: ClassPerfQuestionRow) {
  const wrong = q.options.filter((o) => !o.is_correct && o.count > 0);
  if (q.question_type !== 'multiple_choice' || wrong.length === 0) return null;
  return wrong.reduce((a, b) => (b.count > a.count ? b : a));
}

export default function QuestionsTable({ questions }: Props) {
  const [openId, setOpenId] = useState<number | null>(null);
  const { rows, sort, toggle } = useSortableTable(questions, QUESTIONS_COLUMNS, { key: 'pct_wrong', dir: 'desc' });
  const pg = usePagination(rows, 10, `${sort?.key}${sort?.dir}`);

  return (
    <div>
      <Table minWidth={780}>
        <thead>
          <tr>
            <SortableTh columnKey="question" sort={sort} onToggle={toggle} className={TH_RULE}>Question</SortableTh>
            <SortableTh columnKey="subject" sort={sort} onToggle={toggle} className={TH_RULE}>Subject</SortableTh>
            <SortableTh columnKey="chapter" sort={sort} onToggle={toggle} className={TH_RULE}>Chapter</SortableTh>
            <SortableTh columnKey="pct_wrong" sort={sort} onToggle={toggle} align="right" help="pct_wrong" className={TH_RULE}>% wrong</SortableTh>
            <SortableTh columnKey="avg_time" sort={sort} onToggle={toggle} align="right" help="question_avg_time" className={TH_RULE}>Avg time</SortableTh>
            <th className={`px-2.5 pb-2.5 ${TH_RULE}`}><span className="sr-only">Expand</span></th>
          </tr>
        </thead>
        <tbody>
          {pg.pageItems.map((q) => {
            const open = openId === q.id;
            const trap = commonestWrong(q);
            const cell = open ? 'bg-panel' : '';
            const flip = () => setOpenId(open ? null : q.id);
            return (
              <Fragment key={q.id}>
                <tr className="cursor-pointer" onClick={flip}>
                  <Td className={cell}>
                    <button
                      type="button"
                      aria-expanded={open}
                      className="block max-w-[440px] truncate rounded-[6px] bg-transparent p-0 text-left font-medium text-ink outline-none focus-visible:ring-2 focus-visible:ring-accent"
                      title={q.ques_excerpt}
                      onClick={(e) => { e.stopPropagation(); flip(); }}>
                      {q.ques_excerpt}
                    </button>
                  </Td>
                  <Td className={`${cell} text-[13px]`}>{q.subject ?? '—'}</Td>
                  <Td className={`${cell} text-[13px]`}>{q.chapter ?? '—'}</Td>
                  <Td align="right" className={cell}>
                    <Badge tone={q.pct_wrong >= 50 ? 'danger' : 'warning'}>{q.pct_wrong.toFixed(0)}%</Badge>
                  </Td>
                  <Td align="right" className={`${cell} whitespace-nowrap font-mono font-medium text-muted`}>{fmtTime(q.avg_time_seconds)}</Td>
                  <Td align="right" className={`${cell} text-muted`}>
                    <ChevronRight size={18} strokeWidth={2} aria-hidden className={`inline transition-transform ${open ? 'rotate-90' : ''}`} />
                  </Td>
                </tr>
                {open && (
                  <tr>
                    <td colSpan={6} className="bg-panel px-[18px] pb-5 pt-1">
                      <div className="mb-3.5 mt-1.5 text-[15px] font-medium text-ink" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(q.ques_html) }} />
                      {q.question_type === 'multiple_choice' && q.options.length > 0 ? (
                        <>
                          <ul>
                            {q.options.map((opt, j) => {
                              const pctOfTotal = Math.max(0, Math.min(100, (opt.count / (q.attempts || 1)) * 100));
                              return (
                                <li key={opt.label} className="grid grid-cols-[30px_minmax(0,1fr)_44px] items-center gap-x-3 gap-y-1 border-t border-track py-[7px] sm:grid-cols-[30px_minmax(0,1fr)_minmax(90px,200px)_44px]">
                                  <span className={`grid h-[26px] w-[26px] place-items-center rounded-[8px] border font-mono text-xs font-semibold ${
                                    opt.is_correct ? 'border-success bg-success text-white' : 'border-line-strong bg-surface'}`}>
                                    {String.fromCharCode(65 + j)}
                                  </span>
                                  <span className="min-w-0 truncate" title={opt.text}>
                                    {opt.text}{opt.is_correct && <> <span className="text-xs font-semibold text-success">Correct</span></>}
                                  </span>
                                  <div role="progressbar" aria-label={`${opt.label}: ${opt.count} of ${q.attempts} picked it`}
                                    aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pctOfTotal)}
                                    className="order-last col-span-3 h-2 overflow-hidden rounded-full bg-track sm:order-none sm:col-span-1">
                                    <div className={`h-full rounded-full ${opt.is_correct ? 'bg-success' : 'bg-accent-ink/50'}`} style={{ width: `${pctOfTotal}%` }} />
                                  </div>
                                  <span className="text-right font-mono font-medium tabular-nums">{opt.count}</span>
                                </li>
                              );
                            })}
                          </ul>
                          <p className="mt-2.5 text-xs text-muted">
                            {q.attempts} attempts{trap ? ` · most common wrong answer: ${trap.text}` : ''}
                          </p>
                        </>
                      ) : (
                        <p className="text-xs text-muted">
                          Distractor breakdown not available for this question type.
                        </p>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </Table>
      <PagedFooter className="mt-3.5" label="Questions pagination" {...pg} onPage={pg.setPage} />
    </div>
  );
}
